import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (builder list/modal from the design system)
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, Clock, Video, MapPin, User as UserIcon, LinkIcon, Plus, X, Rocket } from 'lucide-react';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import OnboardingBatchModal from '../components/OnboardingBatchModal';
import { AccessDeniedPanel } from '../components/AccessDeniedPanel';
import { NoProjectSelectedState } from '../components/NoProjectSelectedState';
import { Skeleton } from '../components/Skeleton';
import { onboardingAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

/**
 * Grow → Onboarding Setup → Journey.
 *
 * Authors the per-project ONBOARDING JOURNEY: the sessions a new joiner attends
 * across their first fortnight, on joiner-relative days (Day 0 = their joining
 * day, whatever the date). Also sets which of those days ARE the shadow-training
 * block, so Assign Buddy can default a joiner's dates from it.
 *
 * A day legitimately holds several sessions, so — unlike the Sign-off Form —
 * there is no "one thing per day" rule here; sessions are ordered within a day.
 */

interface JourneySession {
  id: number;
  title: string;
  description: string | null;
  fromDay: number;
  toDay: number;
  startTime: string | null;
  durationMinutes: number | null;
  host: string | null;
  meetingLink: string | null;
  mode: string;
  displayOrder: number;
  active: boolean;
  isShadow: boolean;
  // Read-only overlay from the API: the ACTUAL meeting link the most recent batch's
  // joiners have (from the created training), plus that batch's id. Null until a
  // batch is initiated for this activity. The template `meetingLink` above stays
  // the editable value; this only reveals what the latest batch is really using.
  liveMeetingLink?: string | null;
  liveMeetingBatchId?: number | null;
}

const apiMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
const httpStatus = (err: unknown): number | null =>
  (err as { response?: { status?: number } })?.response?.status ?? null;

// Day numbers are 1-BASED for display: Day 1 = the joiner's joining day (Day 2 =
// their 2nd day, and so on). Storage stays 0-based; the builder converts at the
// input boundary (see emptyForm / openEdit / saveSession). from/to here are the
// 1-based display values.
const ordinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
const describeDay = (d: number): string =>
  d === 1 ? 'their joining day' : `their ${ordinal(d)} day`;

const describeRange = (from: number, to: number): string => {
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to < from) {
    return 'Set a valid day range.';
  }
  if (from === to) return `On ${describeDay(from)}.`;
  return `From ${describeDay(from)} through ${describeDay(to)}.`;
};

const dayLabel = (from: number, to: number) => (from === to ? `Day ${from}` : `Day ${from}–${to}`);

/**
 * The save notice, plus how many current joiners' shadow days the edit changed —
 * every save re-aligns live buddy assignments with the Journey from today on.
 */
const withResyncNote = (base: string, body: unknown): string => {
  const n = Number((body as { shadowResync?: { updated?: unknown } | null } | undefined)?.shadowResync?.updated ?? 0);
  return n > 0 ? `${base} Shadow days updated for ${n} current joiner${n === 1 ? '' : 's'}.` : base;
};

const fmtDuration = (mins: number | null): string => {
  if (!mins || mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return [h ? `${h} hr` : '', m ? `${m} min` : ''].filter(Boolean).join(' ');
};

const fmtTime = (t: string | null): string => {
  if (!t) return '';
  const [hh, mm] = t.split(':').map(Number);
  if (Number.isNaN(hh)) return t;
  const ampm = hh < 12 ? 'AM' : 'PM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
};

type SessionForm = {
  title: string;
  description: string;
  fromDay: string;
  toDay: string;
  startTime: string;
  durationHours: string;
  host: string;
  meetingLink: string;
  mode: 'online' | 'in_person';
  isShadow: boolean;
};

const emptyForm = (): SessionForm => ({
  title: '', description: '', fromDay: '1', toDay: '1', startTime: '',
  durationHours: '', host: '', meetingLink: '', mode: 'online', isShadow: false,
});

export default function OnboardingJourneyPage({
  embedded, actionSlot,
}: { embedded?: boolean; actionSlot?: HTMLElement | null } = {}) {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<JourneySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Builder modal — null editingId means "create".
  const [builderOpen, setBuilderOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<SessionForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<JourneySession | null>(null);

  const load = useCallback(async () => {
    if (selectedProjectId == null) return;
    setLoading(true); setError(null); setForbidden(null);
    try {
      const res = await onboardingAPI.getJourneySessions(selectedProjectId);
      setSessions(res.data?.data ?? []);
    } catch (err) {
      if (httpStatus(err) === 403) setForbidden(apiMessage(err, 'You do not have access to onboarding.'));
      else setError(apiMessage(err, 'Failed to load the journey.'));
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setBuilderOpen(true); };
  const openEdit = (s: JourneySession) => {
    setEditingId(s.id);
    setForm({
      title: s.title,
      description: s.description ?? '',
      // Storage is 0-based; the builder shows 1-based (Day 1 = joining day).
      fromDay: String(s.fromDay + 1),
      toDay: String(s.toDay + 1),
      startTime: s.startTime ?? '',
      durationHours: s.durationMinutes ? String(s.durationMinutes / 60) : '',
      host: s.host ?? '',
      meetingLink: s.meetingLink ?? '',
      mode: s.mode === 'in_person' ? 'in_person' : 'online',
      isShadow: s.isShadow,
    });
    setBuilderOpen(true);
  };

  const saveSession = async () => {
    if (selectedProjectId == null) return;
    setError(null);
    const title = form.title.trim();
    if (!title) { setError('Give the activity a title.'); return; }
    // Inputs are 1-based (Day 1 = joining day). Day 0 is not allowed. Convert to
    // 0-based storage after validating.
    const fromDisplay = Number(form.fromDay);
    const toDisplay = Number(form.toDay);
    if (!Number.isInteger(fromDisplay) || fromDisplay < 1) { setError('Start day must be Day 1 or later — Day 1 is the joining day.'); return; }
    if (!Number.isInteger(toDisplay) || toDisplay < fromDisplay) { setError('End day cannot be before the start day.'); return; }
    const fromDay = fromDisplay - 1;
    const toDay = toDisplay - 1;
    // Hours → minutes at the boundary; the API stores minutes.
    const hrs = form.durationHours.trim() === '' ? null : Number(form.durationHours);
    if (hrs != null && (!Number.isFinite(hrs) || hrs <= 0)) { setError('Duration must be a positive number of hours.'); return; }
    const durationMinutes = hrs != null ? Math.round(hrs * 60) : null;

    // Every activity keeps its time/duration/host/link/mode — INCLUDING a
    // shadow-training day. A shadow day is a store placement, but the joiner may
    // ALSO have an online session that day (a WebEx while at the buddy's store),
    // so those fields stay available. `isShadow` only flags the store-placement
    // aspect (Assign Buddy + the mobile store card use it); it no longer wipes them.
    const payload = {
      title, description: form.description.trim() || null, fromDay, toDay,
      startTime: form.startTime.trim() || null, durationMinutes,
      host: form.host.trim() || null, meetingLink: form.meetingLink.trim() || null,
      mode: form.mode, isShadow: form.isShadow,
    };
    setSaving(true);
    try {
      if (editingId == null) {
        const res = await onboardingAPI.createJourneySession({ projectId: selectedProjectId, ...payload });
        setNotice(withResyncNote('Activity added.', res.data));
      } else {
        const res = await onboardingAPI.updateJourneySession({ projectId: selectedProjectId, sessionId: editingId, ...payload });
        setNotice(withResyncNote('Activity updated.', res.data));
      }
      setBuilderOpen(false);
      await load();
    } catch (err) {
      setError(apiMessage(err, 'Failed to save the activity.'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (selectedProjectId == null || !deleteTarget) return;
    try {
      const res = await onboardingAPI.deleteJourneySession({ projectId: selectedProjectId, sessionId: deleteTarget.id });
      setDeleteTarget(null);
      setNotice(withResyncNote('Activity removed.', res.data));
      await load();
    } catch (err) {
      setError(apiMessage(err, 'Failed to delete the activity.'));
      setDeleteTarget(null);
    }
  };

  const orderedSessions = useMemo(
    () => [...sessions].sort((a, b) =>
      a.fromDay - b.fromDay || a.displayOrder - b.displayOrder || (a.startTime ?? '').localeCompare(b.startTime ?? '')),
    [sessions],
  );

  const addButton = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => setBatchOpen(true)}
        className="inline-flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
      >
        <Rocket className="h-4 w-4" /> Initiate Batch
      </button>
      <button
        type="button"
        onClick={openCreate}
        className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
      >
        <Plus className="h-4 w-4" /> Add Activity
      </button>
    </div>
  );

  if (selectedProjectId == null) {
    return (
      <div className="space-y-6">
        {!embedded && <h1 className="text-2xl font-bold text-gray-900">Journey</h1>}
        <NoProjectSelectedState message="Select a project from the dropdown in the menu bar." />
      </div>
    );
  }

  if (forbidden) {
    return <AccessDeniedPanel message={forbidden} onLogout={() => { clearAuth(); navigate('/login'); }} />;
  }

  return (
    <div className="space-y-4">
      {!embedded && <h1 className="text-2xl font-bold text-gray-900">Onboarding Journey</h1>}

      {actionSlot ? createPortal(addButton, actionSlot) : (
        <div className="flex items-center justify-end">{addButton}</div>
      )}

      {batchOpen && selectedProjectId != null && (
        <OnboardingBatchModal
          projectId={selectedProjectId}
          onClose={() => setBatchOpen(false)}
          onDone={(msg) => { setBatchOpen(false); setNotice(msg); }}
        />
      )}

      {notice && (
        <div className="flex items-start justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="text-primary-400 hover:text-primary-700"><X className="h-4 w-4" /></button>
        </div>
      )}
      {error && (
        <div className="flex items-start justify-between gap-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-700"><X className="h-4 w-4" /></button>
        </div>
      )}


      {/* Sessions */}
      {loading ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}</div>
      ) : orderedSessions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-6 py-12 text-center">
          <p className="text-sm font-medium text-gray-700">No activities yet</p>
          <p className="mt-1 text-xs text-gray-500">Add induction, training and other activities the joiner should attend in their first days.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="px-4 py-3">Activity</th>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Time · Duration</th>
                <th className="px-4 py-3">Host</th>
                <th className="px-4 py-3">Mode</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orderedSessions.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{s.title}</span>
                      {s.isShadow && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-700">
                          <CalendarRange className="h-3 w-3" /> Shadow day
                        </span>
                      )}
                    </div>
                    {s.description && <div className="mt-0.5 max-w-md truncate text-xs text-gray-500" title={s.description}>{s.description}</div>}
                    {s.meetingLink && (
                      <a href={s.meetingLink} target="_blank" rel="noreferrer" title="The standing link saved on the template" className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary-600 hover:underline">
                        <LinkIcon className="h-3 w-3" /> Template link
                      </a>
                    )}
                    {s.liveMeetingLink && (
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <a
                          href={s.liveMeetingLink}
                          target="_blank"
                          rel="noreferrer"
                          title="The actual link the latest batch's joiners have (from the created training)"
                          className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline"
                        >
                          <LinkIcon className="h-3 w-3" /> Live link{s.liveMeetingBatchId ? ` · Batch #${s.liveMeetingBatchId}` : ''}
                        </a>
                        {s.meetingLink && s.liveMeetingLink !== s.meetingLink && (
                          <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700" title="The latest batch uses a different link than the template">
                            differs from template
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="whitespace-nowrap font-medium text-gray-800">{dayLabel(s.fromDay + 1, s.toDay + 1)}</div>
                    <div className="text-xs text-gray-500">{describeRange(s.fromDay + 1, s.toDay + 1)}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                    {s.startTime ? fmtTime(s.startTime) : <span className="text-gray-400">—</span>}
                    {s.durationMinutes ? <span className="text-xs text-gray-500"> · {fmtDuration(s.durationMinutes)}</span> : null}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{s.host || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                      {s.mode === 'in_person' ? <MapPin className="h-3 w-3" /> : <Video className="h-3 w-3" />}
                      {s.mode === 'in_person' ? 'In person' : 'Online'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <TableActionGroup>
                      <TableActionButton action="edit" label="Edit"
                        type="button"
                        onClick={() => openEdit(s)}
                        title="Edit"
                      />
                      <TableActionButton action="delete" label="Delete"
                        type="button"
                        onClick={() => setDeleteTarget(s)}
                        title="Delete"
                      />
                    </TableActionGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / edit modal — fixed header + scrollable body + fixed footer, so
          the actions stay visible on a small screen (matches the Sign-off modal). */}
      {builderOpen && createPortal(
        <div
          className="fixed inset-0 z-[110] flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4 py-10"
          role="dialog" aria-modal="true"
          onClick={() => !saving && setBuilderOpen(false)}
        >
          <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h3 className="text-base font-semibold text-gray-900">
                {editingId == null ? (form.isShadow ? 'Add shadow-training day' : 'Add activity') : (form.isShadow ? 'Edit shadow-training day' : 'Edit activity')}
              </h3>
              <button type="button" onClick={() => setBuilderOpen(false)} className="text-gray-400 transition-colors hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>

            {/* Body */}
            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5">
              {/* The shadow-week toggle — pick this instead of adding a separate
                  setting. Exactly one session per project can be the shadow block. */}
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, isShadow: !p.isShadow, mode: p.isShadow ? 'online' : 'in_person' }))}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                  form.isShadow ? 'border-primary-300 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${form.isShadow ? 'bg-primary-600' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${form.isShadow ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                </span>
                <span>
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900"><CalendarRange className="h-4 w-4 text-primary-500" /> This is a shadow-training day</span>
                  <span className="mt-0.5 block text-xs text-gray-500">On these day(s) the joiner shadows a buddy at their store. Mark as many activities as shadow-training days as you need — they don't have to be consecutive. Assign Buddy uses these, and joiners who already have a buddy follow any change from today onwards. Add a time, host or link below only if there's <strong>also</strong> an online session that day.</span>
                </span>
              </button>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">Title</label>
                <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder={form.isShadow ? "e.g. Shadow training at buddy's store" : 'e.g. Induction WebEx'} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">Details <span className="font-normal text-gray-400">(optional)</span></label>
                <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={2} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">Which day(s)</label>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Day</span>
                  {/* No min/max: a native constraint inside a form silently blocks
                      submit; saveSession validates and shows a banner instead. */}
                  <input type="number" value={form.fromDay} onChange={(e) => setForm((p) => ({ ...p, fromDay: e.target.value }))} className="w-20 rounded-lg border border-gray-300 px-2 py-2 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" />
                  <span className="text-gray-600">to Day</span>
                  <input type="number" value={form.toDay} onChange={(e) => setForm((p) => ({ ...p, toDay: e.target.value }))} className="w-20 rounded-lg border border-gray-300 px-2 py-2 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" />
                </div>
                <p className="mt-1.5 text-xs font-medium text-primary-700">{describeRange(Number(form.fromDay), Number(form.toDay))}</p>
                <p className="mt-0.5 text-xs text-gray-500"><strong>Day 1 is the joiner's joining day</strong>, so Day 2 is their second day, and so on.</p>
              </div>

              {/* On a shadow-training day the store placement is implicit (Assign
                  Buddy + the mobile store card use it) — but the joiner may ALSO
                  have an online session that day, so the time/host/link fields stay
                  available. Leave them blank for a pure store day. */}
              {form.isShadow && (
                <div className="flex items-start gap-2 rounded-lg bg-primary-50 px-3 py-2.5 text-xs text-primary-700">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>The joiner shadows a buddy at their store on this day. If they <strong>also</strong> have an online session that day, add its time, host and link below — otherwise leave those blank.</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 flex items-center gap-1 text-xs font-medium text-gray-600"><Clock className="h-3 w-3" /> Start time</label>
                  <input type="time" value={form.startTime} onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">Duration (hours)</label>
                  <input type="number" step="0.5" value={form.durationHours} onChange={(e) => setForm((p) => ({ ...p, durationHours: e.target.value }))} placeholder="e.g. 1.5" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" />
                </div>
              </div>

              <div>
                <label className="mb-1.5 flex items-center gap-1 text-xs font-medium text-gray-600"><UserIcon className="h-3 w-3" /> Host <span className="font-normal text-gray-400">(who runs it)</span></label>
                <input value={form.host} onChange={(e) => setForm((p) => ({ ...p, host: e.target.value }))} placeholder="e.g. Training Team / a name" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">Mode</label>
                <div className="flex gap-2">
                  {(['online', 'in_person'] as const).map((m) => (
                    <button key={m} type="button" onClick={() => setForm((p) => ({ ...p, mode: m }))} className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.mode === m ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {m === 'online' ? <Video className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
                      {m === 'online' ? 'Online' : 'In person'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 flex items-center gap-1 text-xs font-medium text-gray-600"><LinkIcon className="h-3 w-3" /> Meeting link <span className="font-normal text-gray-400">(optional)</span></label>
                <input value={form.meetingLink} onChange={(e) => setForm((p) => ({ ...p, meetingLink: e.target.value }))} placeholder="https://…" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100" />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
              <button type="button" onClick={() => setBuilderOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100">Cancel</button>
              <button type="button" onClick={saveSession} disabled={saving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50">
                {saving ? 'Saving…' : editingId == null ? (form.isShadow ? 'Add shadow day' : 'Add activity') : 'Save changes'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {deleteTarget && (
        <ConfirmDeleteModal
          isOpen
          onClose={() => setDeleteTarget(null)}
          onConfirm={doDelete}
          isSubmitting={false}
          title="Remove activity"
          entityLabel="activity"
          entityName={deleteTarget.title}
          consequenceNote="It will no longer appear in any joiner's onboarding journey."
          confirmLabel="Remove"
        />
      )}
    </div>
  );
}
